import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { avatarItemsApi } from '../services/catalogServiceAPI/avatarItemsProxy';
import { ItemForm, SLOT_LABELS, UNLOCK_LABELS } from './ItemForm';

const { Title, Paragraph } = Typography;

function statusTag(row) {
    if (!row.enabled) return <Tag>Disabled</Tag>;
    if (row.unlock !== 'timed') return <Tag color="green">Live</Tag>;
    const now = Date.now();
    if (new Date(row.availableFrom).getTime() > now) return <Tag color="blue">Scheduled</Tag>;
    if (new Date(row.availableUntil).getTime() <= now) return <Tag color="gold">Ended (owners keep it)</Tag>;
    return <Tag color="green">Claimable now</Tag>;
}

const windowText = (row) => (row.unlock === 'timed'
    ? `${new Date(row.availableFrom).toLocaleString()} → ${new Date(row.availableUntil).toLocaleString()}`
    : '—');

/**
 * Avatar items the apps download and draw: holiday hats, prize props. Adding
 * one here needs no app release. A user gets an item for free, by saving
 * their avatar with it during its window, or by winning it in a challenge.
 */
export function AvatarItems() {
    const [rows, setRows] = useState(null);
    // null = closed, {} = new, a row = editing it.
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try {
            setRows(await avatarItemsApi.list());
        } catch (e) {
            message.error(`Failed to load items: ${e.message}`);
            setRows([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        try {
            await avatarItemsApi.remove(id);
            load();
        } catch (e) {
            message.error(`Failed to delete: ${e.message}`);
        }
    };

    const columns = [
        { title: 'Name', dataIndex: 'name' },
        { title: 'Worn as', width: 120, render: (_, row) => SLOT_LABELS[row.slot] },
        { title: 'Unlocked by', width: 190, render: (_, row) => UNLOCK_LABELS[row.unlock] },
        { title: 'Status', width: 190, render: (_, row) => statusTag(row) },
        { title: 'Claim window', render: (_, row) => windowText(row) },
        { title: 'Owners', width: 90, dataIndex: 'ownerCount' },
        {
            title: '',
            width: 160,
            render: (_, row) => (
                <Space>
                    <Button size="small" onClick={() => setEditing(row)}>Edit</Button>
                    <Popconfirm
                        title="Delete this item?"
                        description={row.ownerCount > 0 ? `${row.ownerCount} users own it and will lose it.` : undefined}
                        onConfirm={() => remove(row.id)}
                    >
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 8 }} align="center">
                <Title level={3} style={{ margin: 0 }}>Avatar items</Title>
                <Button type="primary" onClick={() => setEditing({})}>New item</Button>
            </Space>
            <Paragraph type="secondary">
                Extra headwear and accessories for the avatar editor. Apps pick up a new or changed item within about five minutes, with no release.
                To give an item as a challenge prize, set it to &ldquo;Challenge prize&rdquo; here and choose it in the challenge&rsquo;s rewards.
            </Paragraph>
            {rows === null ? <Spin /> : (
                <Table rowKey="id" dataSource={rows} columns={columns} pagination={false} locale={{ emptyText: 'No items yet' }} />
            )}
            {editing && (
                <ItemForm
                    item={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}
